// src/components/OttiGrid.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid as MuiGrid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from "@mui/material";
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  PlayArrow as RunIcon,
  Save as SaveIcon
} from "@mui/icons-material";
import {
  PamGrid,
  QuickFilter,
  GridConfigMenu,
  useGridConfigs,
  useNotification,
  formatDateTimeForDisplay,
  isPermissionDenied,
  getPermissionDeniedMessage,
  parseExcelWorkbook,
  buildHeaderMappingsFromAliases,
  createColumnNormalizer,
  normalizeRowKeys,
  mergeRows,
  coerceNumeric,
  coerceDate,
  notifyPermissionError,
  getWriteErrorMessage,
  withLoadedModifiedDate,
  VERSION_CONFLICT_MESSAGE,
} from '@1823-partners/core';

import api, {
  fetchOttiData,
  fetchOttiDates,
  runOtti,
  getRunStatus,
  saveOtti,
  fetchPreviousLotIds,
  getCurrentSpreadUploadStatus,
  getRunHistory,
  uploadCurrentSpreads,
  uploadRatingsDowngrades
} from "../api";
import {
  formatDate,
  computeRowFlags,
  FLAG_INPUT_FIELDS,
  isMissingComment,
  needsCommentRows,
  partitionOttiBySecOwner,
  formatCurrencyCell,
  formatDeltaCell,
  formatRatePercentCell,
  formatBpsCell,
  formatIntegerCell,
  parseNumericEdit,
} from "../utils/ottiUtils";
import { detectAndParse, mapCurrentSpreadPayload, mapRatingsDowngradePayload } from "../utils/sheetRouter";
import { reapplyEdits } from "../utils/pendingEdits";
import ottiColumnMapping from "../data/ottiColumnMapping.json";

// Column definitions - moved outside component to avoid recreating on every render
const COLUMN_DEFINITIONS = {
  LOT_ID: { type: 'VARCHAR', editable: false },
  SRC_LOT_ID: { type: 'VARCHAR', editable: false },
  ID: { type: 'VARCHAR', editable: false },
  DES: { type: 'VARCHAR', editable: false },
  DES2: { type: 'VARCHAR', editable: false },
  CLNT_NAME: { type: 'VARCHAR', editable: false },
  DT: { type: 'DATE', editable: true },
  SEC_OWNER: { type: 'VARCHAR', editable: false },
  ST: { type: 'VARCHAR', editable: false },
  STYPEL3: { type: 'VARCHAR', editable: false },
  WATCHLIST_TRIGGERS: { type: 'VARCHAR', editable: false },
  MDT: { type: 'DATE', editable: true },
  MYLD: { type: 'FLOAT', editable: false },
  IRR: { type: 'FLOAT', editable: false },
  GIRR: { type: 'FLOAT', editable: false },
  SIRR: { type: 'FLOAT', editable: false },
  DAYS: { type: 'NUMBER', editable: false },
  MPX: { type: 'FLOAT', editable: false },
  BPX: { type: 'FLOAT', editable: false },
  G_BPX: { type: 'FLOAT', editable: false },
  S_BPX: { type: 'FLOAT', editable: false },
  FV: { type: 'FLOAT', editable: false },
  BV: { type: 'FLOAT', editable: false },
  G_BV: { type: 'FLOAT', editable: false },
  S_BV: { type: 'FLOAT', editable: false },
  MV: { type: 'FLOAT', editable: false },
  G_MV: { type: 'FLOAT', editable: false },
  S_MV: { type: 'FLOAT', editable: false },
  NPV: { type: 'FLOAT', editable: false },
  G_NPV: { type: 'FLOAT', editable: false },
  S_NPV: { type: 'FLOAT', editable: false },
  DELTA: { type: 'FLOAT', editable: false },
  G_DELTA: { type: 'FLOAT', editable: false },
  S_DELTA: { type: 'FLOAT', editable: false },
  CURR_SPRD: { type: 'FLOAT', editable: true },
  ORIG_SPRD: { type: 'FLOAT', editable: true },
  SPRD_FLG: { type: 'VARCHAR', editable: false },
  OTTI_FLG: { type: 'VARCHAR', editable: false },
  COMMENTS: { type: 'VARCHAR', editable: true },
  MODIFIED_DATE: { type: 'TIMESTAMP_NTZ', editable: false },
  MODIFIED_BY: { type: 'VARCHAR', editable: false },
  KEEP_COMMENT: { type: 'BOOLEAN', editable: true },
  ASSUMPTIONS: { type: 'VARCHAR', editable: true },
  RATING_DOWNGRADE: { type: 'VARCHAR', editable: true },
  PAYMENT_TERM_CHANGE: { type: 'VARCHAR', editable: true },
  EXPECTED_MATURITY: { type: 'VARCHAR', editable: true },
  LD_DT: { type: 'DATE', editable: false }
};

// Rating downgrades are calculated and displayed for ALL securities (SP + Non-SP),
// so RATING_DOWNGRADE is shown on both grids. The spread check is structured-products
// only (CURR_SPRD / ORIG_SPRD / ASSUMPTIONS are hidden on the Non-SP grid). Payment
// term change / expected maturity remain Non-SP only.
const SP_EXCLUDED_COLUMNS = ['PAYMENT_TERM_CHANGE', 'EXPECTED_MATURITY'];
const NON_SP_EXCLUDED_COLUMNS = ['CURR_SPRD', 'ORIG_SPRD', 'ASSUMPTIONS'];
const SYSTEM_FIELDS = ['MODIFIED_BY', 'MODIFIED_DATE'];
// Row identity everywhere in this grid (matches getRowId and changedRows).
const rowKey = (row) => row.LOT_ID || row.ID;
// Upload-merge key property (see applyLotDetailRows); never part of row data.
const MERGE_KEY = Symbol('ottiMergeKey');
const withoutMergeKey = ({ [MERGE_KEY]: _mergeKey, ...row }) => row;

// Any 409 means the rows on screen are stale: reload, keeping the user's edits.
// Deliberately not core's isVersionConflict, which is only the writer's
// version_conflict: Run OTTI's run-date-mismatch 409 ({ error: "<message>" })
// must take the same path. getWriteErrorMessage shows that 409's own text.
const isStaleRowsError = (err) => err?.response?.status === 409;

// reapplyEdits `derive`: fresh flags for a merged row whose edits touch a
// flag input, as onCellValueChanged computes them for a live edit.
const reflagEdited = (row, changedFields) =>
  [...changedFields].some((field) => FLAG_INPUT_FIELDS.includes(field))
    ? computeRowFlags(row)
    : null;

// Run OTTI can outlast the SWA gateway (the proxy 500s while Beacon keeps
// working). When that happens we poll /run-status until the run commits its
// rows — ~10 minutes at a 15s cadence.
const RUN_POLL_INTERVAL_MS = 15000;
const RUN_POLL_MAX_ATTEMPTS = 40;

// Shown when Run / Save / Upload is attempted while the grid does not hold the
// selected date's rows (its load failed).
const GRID_NOT_LOADED_MESSAGE =
  "The OTTI data for the selected date is not loaded. Click Refresh to reload it, then try again.";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const runStatusChipColor = (status) => {
  switch (String(status || '').toUpperCase()) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'IN PROGRESS':
      return 'warning';
    default:
      return 'default';
  }
};

// Build the 1823-core import alias map for client-side Excel upload. Mirrors
// secmaster's CW_IMPORT_ALIASES pattern: map every DB column that carries a
// human 'name' (from ottiColumnMapping.json — the same map the old server-side
// /upload-otti endpoint used) to [humanHeader, DB_COLUMN]. We always accept the
// DB column name directly as well so files exported by this app re-import cleanly.
const OTTI_IMPORT_ALIASES = Object.entries(ottiColumnMapping).reduce((acc, [dbColumn, field]) => {
  const aliases = [dbColumn];
  if (field && field.name) {
    aliases.push(field.name);
  }
  acc[dbColumn] = aliases;
  return acc;
}, {});
const OTTI_IMPORT_MAPPINGS = buildHeaderMappingsFromAliases(OTTI_IMPORT_ALIASES);
const ottiColumnNormalizer = createColumnNormalizer(OTTI_IMPORT_MAPPINGS);

// The derived flags: computeRowFlags is their single frontend source of truth,
// so an uploaded "Sprd Flg" / "Otti Flg" column is never an import target.
const DERIVED_FLAG_FIELDS = ['SPRD_FLG', 'OTTI_FLG'];

// Only DB columns that have a human 'name' are valid import targets.
const OTTI_IMPORT_FIELDS = new Set(
  Object.entries(ottiColumnMapping)
    .filter(([dbColumn, field]) => field && field.name && !DERIVED_FLAG_FIELDS.includes(dbColumn))
    .map(([dbColumn]) => dbColumn)
);

// "Keep Comment (Y/N)": Y/Yes/TRUE/1 or N/No/FALSE/0; anything else is unparseable.
const coerceImportBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  if (['y', 'yes', 'true', '1'].includes(text)) return true;
  if (['n', 'no', 'false', '0'].includes(text)) return false;
  return null;
};
const IMPORT_COERCERS = { numeric: coerceNumeric, date: coerceDate, boolean: coerceImportBoolean };
const importCoercerFor = (field) => IMPORT_COERCERS[ottiColumnMapping[field]?.datatype];

// An uploaded cell normalized to what a live edit stores: a number for numeric
// columns (parseNumericEdit), 'YYYY-MM-DD' for dates (the date valueParser), a
// boolean for KEEP_COMMENT. A blank cell is null. A non-empty value that does
// not parse is `invalid`: like parseNumericEdit, it never replaces a stored value.
const normalizeImportValue = (field, raw) => {
  const coerce = importCoercerFor(field);
  if (!coerce) return { value: raw, invalid: false };
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return { value: null, invalid: false };
  }
  const value = coerce(raw);
  return { value, invalid: value === null };
};

// Whether a normalized uploaded value differs from the row's current one
// (compared as the same type, so 250 vs '250.0' is not a change).
const importValueChanged = (field, existing, value) => {
  const coerce = importCoercerFor(field);
  if (coerce) {
    const current = existing === null || existing === undefined || String(existing).trim() === ''
      ? null
      : coerce(existing);
    return current !== value;
  }
  const existingStr = existing != null ? String(existing).trim() : '';
  const newStr = value != null ? String(value).trim() : '';
  return existingStr !== newStr;
};

const OttiGrid = ({ userId }) => {
  const { showNotification } = useNotification();
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);

  // Asset class assumptions data
  const assumptionsData = [
    ["Asset Class", "Prepayment", "Default/Recovery", "Call"],
    [
      "LOGN II & III",
      "15% CPR in line with the market \n convention / reflecting slower speeds with higher rates",
      "4% default rate for 18 months (longer default cycle, better vs. regular CLOs given premium manager) followed by a 1.5% default rate thereafter. Estimating 60% recovery",
      "Call 12 months after Reinvestment Period ends",
    ],
    [
      "Anchorage",
      "3% CPR (blended per collateral composition)",
      "Bonds: 6% default rate for 24 months (longer default cycle) followed by a 4% default rate thereater.  Estimating 40% recovery; Loans: 4% default rate for 18 months (longer default cycle) followed by a 2% rate thereafter.  Estimating 60% recovery",
      "Call 12 months after Reinvestment Period ends",
    ],
    ["Ground Lease", "", "Pays on a fixed schedule. No additional CF assumptions.", ""],
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [isRunningOtti, setIsRunningOtti] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [changedRows, setChangedRows] = useState(new Set());
  const [changedCells, setChangedCells] = useState(new Map());
  const [newLotIds, setNewLotIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [ottiData, setOttiData] = useState([]);
  const [spData, setSpData] = useState([]);
  const [nonSpData, setNonSpData] = useState([]);
  const [spColumnDefs, setSpColumnDefs] = useState([]);
  const [nonSpColumnDefs, setNonSpColumnDefs] = useState([]);
  const [quickFilterText, setQuickFilterText] = useState("");
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [spreadUploadStatus, setSpreadUploadStatus] = useState({
    asofdt: null,
    checked: false,
    loading: false,
    uploaded: false,
    rowCount: 0,
    latestUploaded: null,
    error: false,
  });
  const [runHistory, setRunHistory] = useState({
    asofdt: null,
    loading: true,
    error: false,
    runs: [],
  });

  const spGridRef = useRef(null);
  const nonSpGridRef = useRef(null);
  const ottiDataRequestRef = useRef(0);
  const spreadStatusRequestRef = useRef(0);
  const runHistoryRequestRef = useRef(0);
  // Rows as the server last returned them, by rowKey. A save takes each row's
  // MODIFIED_DATE from here (never from the edited row), so the writer can
  // 409 when someone else saved that lot since this load.
  const loadedRowsRef = useRef(new Map());
  // The as-of date whose rows are in the grid, set only by a successful load.
  // Run / Save / Upload act only when it equals selectedDate, so a failed load
  // of a newly selected date can never run, save or upload the previous date's
  // rows. The state disables the buttons; the ref is what the handlers read.
  const [loadedDate, setLoadedDate] = useState(null);
  const loadedDateRef = useRef(null);
  // Hidden file input driven by the Upload toolbar button (so the button keeps
  // its full disabled logic — PamGrid's built-in upload button can't be disabled).
  const fileInputRef = useRef(null);

  // Check if editing should be enabled (only on maximum available date)
  const isEditingEnabled = useCallback(() => {
    if (!availableDates.length || !selectedDate) return false;
    const maxDate = availableDates.reduce((max, current) => current > max ? current : max, availableDates[0]);
    return selectedDate === maxDate;
  }, [availableDates, selectedDate]);

  // Check if the selected date is the maximum available date
  const isMaxAvailableDate = useCallback(() => {
    if (!selectedDate || !availableDates.length) return false;
    const maxDate = availableDates.reduce((max, current) => current > max ? current : max, availableDates[0]);
    return selectedDate === maxDate;
  }, [selectedDate, availableDates]);

  // Check if the selected date is >= 9/30/25
  const isDateAfterCutoff = useCallback(() => {
    if (!selectedDate) return false;
    return selectedDate >= '2025-09-30';
  }, [selectedDate]);

  // Create column definition for AG-Grid
  const createColumnDef = useCallback((field, fieldType, isSchemaEditable, changedCellsMap = new Map(), newLotIdsSet = new Set(), forExport = false) => {
    const editingEnabled = isEditingEnabled();
    const isFieldEditable = isSchemaEditable && !SYSTEM_FIELDS.includes(field) && editingEnabled;
    const baseHeaderName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const headerName = (isFieldEditable && !forExport) ? `✏️ ${baseHeaderName}` : baseHeaderName;

    const column = {
      field: field,
      headerName: headerName,
      headerTooltip: isFieldEditable ? `This column is editable. Click on any cell to modify values.` : undefined,
      flex: 1,
      minWidth: 120,
      resizable: true,
      sortable: true,
      filter: true,
      editable: isFieldEditable,
      // Inline cellStyle (not cellClass) so highlighting renders under PamGrid's
      // themeQuartz — the old .ag-theme-alpine-scoped CSS no longer matched.
      // Base 12px font; change-tracking wins over flag/comment/editable cues.
      cellStyle: params => {
        const rowId = params.data?.LOT_ID || params.data?.ID || params.node?.id;
        const isCellChanged = changedCellsMap.has(rowId) && changedCellsMap.get(rowId)?.has(field);
        const isRowChanged = changedCellsMap.has(rowId) && changedCellsMap.get(rowId)?.size > 0;
        const isNewLotId = newLotIdsSet.has(params.data?.LOT_ID);
        const editable = isSchemaEditable && !SYSTEM_FIELDS.includes(field) && editingEnabled;

        let style = { fontSize: '12px' };
        if (editable) {
          style = { ...style, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', cursor: 'pointer' };
        }
        if (SYSTEM_FIELDS.includes(field)) {
          style = { ...style, backgroundColor: '#f0f0f0', color: '#666666', fontStyle: 'italic', fontSize: '11px' };
        }
        // Whole-row "changed" tint — applied first so the semantic signals
        // (new-lot / flags / missing-comment) below override it, and so a
        // new-lot cell never ends up with a tan fill behind its green border.
        if (isRowChanged) {
          style = { ...style, backgroundColor: '#ffe6cc' };
        }
        if (field === 'LOT_ID' && isNewLotId && !params.node?.rowPinned) {
          style = { ...style, backgroundColor: '#e8f5e8', fontWeight: 'bold', border: '2px solid #4caf50' };
        }
        if (field === 'OTTI_FLG' && (params.value === 'Y' || params.value === 'YES' || params.value === true || params.value === 'Cashflow Exception')) {
          style = { ...style, backgroundColor: '#ffebee', color: 'red', fontWeight: 'bold' };
        } else if (field === 'SPRD_FLG' && params.value === 'FLAG') {
          style = { ...style, backgroundColor: '#fff3cd', color: '#856404', fontWeight: 'bold' };
        } else if (field === 'COMMENTS' && isMissingComment(params.data)) {
          style = { ...style, outline: '1px dashed #856404', outlineOffset: '-3px', fontStyle: 'italic' };
        }
        // The specific edited cell always wins (most important transient cue).
        if (isCellChanged) {
          style = { ...style, backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold' };
        }
        return style;
      }
    };

    // Add field-specific formatting
    if (field === 'WATCHLIST_TRIGGERS') {
      column.headerName = 'Watchlist Trigger(s)';
      column.flex = 2;
      column.minWidth = 280;
      column.headerTooltip = 'Rule or rules that caused this lot to enter the OTTI watchlist.';
      column.tooltipValueGetter = params => params.value || '';
    } else if (field === 'MODIFIED_DATE') {
      column.valueFormatter = params => {
        if (!params.value) return '';
        return formatDateTimeForDisplay(params.value) || '';
      };
    } else if (['DT', 'MDT', 'LD_DT'].includes(field)) {
      column.valueFormatter = params => {
        if (!params.value) return '';
        if (typeof params.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(params.value)) {
          const parts = params.value.split('-');
          return `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        return formatDate(params.value, 'MM/DD/YYYY');
      };
      if (isSchemaEditable) {
        column.cellEditor = 'agDateCellEditor';
        column.valueParser = params => {
          if (!params.newValue) return null;
          return formatDate(params.newValue, 'YYYY-MM-DD');
        };
      }
    } else if (['FV', 'BV', 'MV', 'NPV', 'G_BV', 'G_MV', 'G_NPV', 'S_BV', 'S_MV', 'S_NPV'].includes(field)) {
      column.type = 'numericColumn';
      column.valueFormatter = params => formatCurrencyCell(params.value);
    } else if (['DELTA', 'G_DELTA', 'S_DELTA'].includes(field)) {
      column.type = 'numericColumn';
      column.valueFormatter = params => formatDeltaCell(params.value);
    } else if (field === 'MYLD') {
      column.type = 'numericColumn';
    } else if (['IRR', 'GIRR', 'SIRR'].includes(field)) {
      column.type = 'numericColumn';
      column.valueFormatter = params => formatRatePercentCell(params.value);
    } else if (['MPX', 'BPX', 'G_BPX', 'S_BPX'].includes(field)) {
      column.type = 'numericColumn';
      column.valueFormatter = params => formatCurrencyCell(params.value);
    } else if (['CURR_SPRD', 'ORIG_SPRD'].includes(field)) {
      column.type = 'numericColumn';
      column.valueFormatter = params => formatBpsCell(params.value);
      if (isSchemaEditable) {
        column.cellEditor = 'agNumberCellEditor';
        column.valueParser = params => parseNumericEdit(params.newValue, params.oldValue);
      }
    } else if (field === 'KEEP_COMMENT') {
      if (isFieldEditable) {
        column.cellRenderer = params => {
          if (params.value === null || params.value === undefined) return 'No';
          return params.value ? 'Yes' : 'No';
        };
        column.cellEditor = 'agSelectCellEditor';
        column.cellEditorParams = { values: ['No', 'Yes'] };
        column.valueParser = params => params.newValue === 'Yes';
        column.singleClickEdit = true;
      } else {
        column.valueFormatter = params => {
          if (params.value === null || params.value === undefined) return '';
          return params.value ? 'Yes' : 'No';
        };
      }
    } else if (field === 'DAYS') {
      column.type = 'numericColumn';
      column.valueFormatter = params => formatIntegerCell(params.value);
    } else if (fieldType === 'VARCHAR' && isSchemaEditable && ['ASSUMPTIONS', 'COMMENTS', 'RATING_DOWNGRADE', 'PAYMENT_TERM_CHANGE', 'EXPECTED_MATURITY'].includes(field)) {
      column.cellEditor = 'agLargeTextCellEditor';
      column.cellEditorPopup = true;
      column.cellEditorParams = { maxLength: 1000, rows: 5, cols: 50 };
    }

    if (fieldType === 'FLOAT' || fieldType === 'NUMBER') {
      if (!column.type) {
        column.type = 'numericColumn';
      }
    }

    return column;
  }, [isEditingEnabled]);

  // Get column definitions for specific grid type
  const getColumnDefs = useCallback((gridType = 'all', changedCellsMap = new Map(), newLotIdsSet = new Set(), forExport = false) => {
    let fieldsToInclude = Object.keys(COLUMN_DEFINITIONS);

    if (gridType === 'sp') {
      fieldsToInclude = fieldsToInclude.filter(field => !SP_EXCLUDED_COLUMNS.includes(field));
    } else if (gridType === 'nonsp') {
      fieldsToInclude = fieldsToInclude.filter(field => !NON_SP_EXCLUDED_COLUMNS.includes(field));
    }

    const isAfterCutoff = isDateAfterCutoff();
    fieldsToInclude = fieldsToInclude.filter(field => {
      return isAfterCutoff
        ? !['BPX', 'BV', 'MV', 'NPV', 'DELTA', 'IRR'].includes(field)
        : !(field.startsWith('G_') || field.startsWith('S_') || field === 'GIRR' || field === 'SIRR');
    });

    return fieldsToInclude.map(field => {
      const fieldDef = COLUMN_DEFINITIONS[field];
      return createColumnDef(field, fieldDef.type, fieldDef.editable, changedCellsMap, newLotIdsSet, forExport);
    });
  }, [isDateAfterCutoff, createColumnDef]);

  // Load previous lot IDs for comparison
  const loadPreviousLotIds = useCallback(async (asofdt) => {
    if (!asofdt) return new Set();
    try {
      const response = await fetchPreviousLotIds(asofdt);
      return new Set(response.data || []);
    } catch (err) {
      console.error("Error fetching previous lot IDs:", err);
      return new Set();
    }
  }, []);

  // Compare current lot IDs with previous month to identify new ones
  const identifyNewLotIds = useCallback((currentData, previousLotIdsSet) => {
    const currentLotIds = currentData.map(row => row.LOT_ID).filter(Boolean);
    return new Set(currentLotIds.filter(lotId => !previousLotIdsSet.has(lotId)));
  }, []);

  // Load available dates
  const loadOttiDates = useCallback(async () => {
    try {
      setIsLoadingDates(true);
      const response = await fetchOttiDates();
      const rawDates = response.data || [];

      const formattedDates = rawDates.map(date => {
        if (typeof date === 'string' && date.includes('GMT')) {
          const parts = date.split(' ');
          if (parts.length >= 4) {
            const day = parts[1];
            const monthName = parts[2];
            const year = parts[3];
            const monthMap = {
              'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
              'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
              'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
            };
            const month = monthMap[monthName];
            if (month) return `${year}-${month}-${day.padStart(2, '0')}`;
          }
        }
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        return formatDate(date, 'YYYY-MM-DD');
      }).filter(date => date && date.length > 0);

      setAvailableDates(formattedDates);
      return formattedDates;
    } catch (err) {
      console.error("Error fetching OTTI dates:", err);
      if (isPermissionDenied(err)) {
        setError(getPermissionDeniedMessage(err));
      } else {
        setError("Failed to load available dates. Please try again.");
      }
      return [];
    } finally {
      setIsLoadingDates(false);
    }
  }, []);

  // Load OTTI data. `keepEdits` ({ rows, changedRows, changedCells }) re-applies
  // the user's unsaved edits on top of the fresh rows (used after a 409).
  // Only the latest call may touch the grid: a slower earlier response (e.g. a
  // historical date) must never land under a newer selectedDate, where it would
  // be editable and saveable.
  const loadOttiData = useCallback(async (asofdt, { keepEdits = null } = {}) => {
    if (!asofdt) return;
    const requestId = ++ottiDataRequestRef.current;

    try {
      setIsLoading(true);
      setError(null);

      const [response, previousLotIdsSet] = await Promise.all([
        fetchOttiData(asofdt),
        loadPreviousLotIds(asofdt)
      ]);
      if (requestId !== ottiDataRequestRef.current) return;

      const fresh = response.data || [];
      loadedRowsRef.current = new Map(fresh.map((row) => [rowKey(row), row]));
      // A merged row mixes this user's inputs with the other person's save,
      // so its flags are recomputed (computeRowFlags stays the one source).
      const merged = keepEdits
        ? reapplyEdits(fresh, keepEdits.rows, keepEdits.changedCells, rowKey, reflagEdited)
        : null;
      const data = merged ? merged.rows : fresh;
      const cells = merged ? merged.changedCells : new Map();
      const newLotIdsSet = identifyNewLotIds(data, previousLotIdsSet);
      setNewLotIds(newLotIdsSet);

      const { sp: structuredProducts, nonSp: nonStructuredProducts } = partitionOttiBySecOwner(data);

      const spColumns = getColumnDefs('sp', cells, newLotIdsSet);
      const nonSpColumns = getColumnDefs('nonsp', cells, newLotIdsSet);

      setSpColumnDefs(spColumns);
      setNonSpColumnDefs(nonSpColumns);
      setOttiData(data);
      setSpData(structuredProducts);
      setNonSpData(nonStructuredProducts);
      setHasUnsavedChanges(Boolean(keepEdits && keepEdits.changedRows.size > 0));
      setChangedRows(keepEdits ? new Set(keepEdits.changedRows) : new Set());
      setChangedCells(cells);
      loadedDateRef.current = asofdt;
      setLoadedDate(asofdt);
      return data;
    } catch (err) {
      if (requestId !== ottiDataRequestRef.current) return;
      console.error("Error fetching OTTI data:", err);
      if (asofdt !== loadedDateRef.current) {
        // The grid still holds another date's rows (the user switched dates and
        // this date failed). Clear them, their edits and their save tokens, so
        // nothing of the old date can be run, saved or uploaded under this one.
        // A failed refresh of the same date keeps everything, unsaved edits too.
        loadedRowsRef.current = new Map();
        loadedDateRef.current = null;
        setLoadedDate(null);
        setOttiData([]);
        setSpData([]);
        setNonSpData([]);
        setNewLotIds(new Set());
        setHasUnsavedChanges(false);
        setChangedRows(new Set());
        setChangedCells(new Map());
        setSpColumnDefs(getColumnDefs('sp', new Map(), new Set()));
        setNonSpColumnDefs(getColumnDefs('nonsp', new Map(), new Set()));
      }
      if (isPermissionDenied(err)) {
        setError(getPermissionDeniedMessage(err));
      } else {
        setError("Failed to load OTTI data. Please try again.");
      }
    } finally {
      if (requestId === ottiDataRequestRef.current) setIsLoading(false);
    }
  }, [getColumnDefs, loadPreviousLotIds, identifyNewLotIds]);

  const loadSpreadUploadStatus = useCallback(async (asofdt) => {
    if (!asofdt) return;
    const requestId = ++spreadStatusRequestRef.current;
    setSpreadUploadStatus({
      asofdt,
      checked: false,
      loading: true,
      uploaded: false,
      rowCount: 0,
      latestUploaded: null,
      error: false,
    });
    try {
      const response = await getCurrentSpreadUploadStatus(asofdt);
      if (requestId !== spreadStatusRequestRef.current) return;
      const status = response.data || {};
      setSpreadUploadStatus({
        asofdt,
        checked: true,
        loading: false,
        uploaded: Boolean(status.uploaded),
        rowCount: Number(status.row_count) || 0,
        latestUploaded: status.latest_uploaded || null,
        error: false,
      });
    } catch (err) {
      if (requestId !== spreadStatusRequestRef.current) return;
      console.error("Error fetching current spread upload status:", err);
      setSpreadUploadStatus({
        asofdt,
        checked: true,
        loading: false,
        uploaded: false,
        rowCount: 0,
        latestUploaded: null,
        error: true,
      });
    }
  }, []);

  const loadRunHistory = useCallback(async (asofdt, { reset = true } = {}) => {
    if (!asofdt) return;
    const requestId = ++runHistoryRequestRef.current;
    if (reset) {
      setRunHistory({
        asofdt,
        loading: true,
        error: false,
        runs: [],
      });
    }
    try {
      const response = await getRunHistory(asofdt);
      if (requestId !== runHistoryRequestRef.current) return;
      const runs = Array.isArray(response.data) ? response.data : [];
      setRunHistory({
        asofdt,
        loading: false,
        error: false,
        runs,
      });
    } catch (err) {
      if (requestId !== runHistoryRequestRef.current) return;
      console.error("Error fetching OTTI run history:", err);
      if (reset) {
        setRunHistory({
          asofdt,
          loading: false,
          error: true,
          runs: [],
        });
      }
    }
  }, []);

  // Initialize dates on mount. Selecting the first date is enough: the
  // selectedDate effect below loads its data (loading it here too fetched it twice).
  useEffect(() => {
    const initializeData = async () => {
      const dates = await loadOttiDates();
      if (dates.length > 0) {
        setSelectedDate(dates[0]);
      }
    };
    initializeData();
  }, [loadOttiDates]);

  // Load data when selected date changes
  useEffect(() => {
    if (selectedDate && availableDates.includes(selectedDate)) {
      loadOttiData(selectedDate);
      loadSpreadUploadStatus(selectedDate);
      loadRunHistory(selectedDate);
    }
  }, [selectedDate, availableDates, loadOttiData, loadSpreadUploadStatus, loadRunHistory]);

  // Poll run history while any listed run is still IN PROGRESS so this
  // session (and other users looking at the same month) see live status.
  // Also poll while this session's Run OTTI is in flight — history starts
  // empty/completed, so waiting for an IN PROGRESS row would hide live
  // status until runOtti settles (up to the 4-minute gateway timeout).
  useEffect(() => {
    const hasInProgress = runHistory.runs.some(
      (run) => String(run.STATUS || '').toUpperCase() === 'IN PROGRESS'
    );
    if (!selectedDate || runHistory.error || (!hasInProgress && !isRunningOtti)) {
      return undefined;
    }
    const timer = setInterval(() => {
      loadRunHistory(selectedDate, { reset: false });
    }, RUN_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [selectedDate, runHistory.runs, runHistory.error, loadRunHistory, isRunningOtti]);

  // Handle cell value changes
  const onCellValueChanged = useCallback((params) => {
    const { data, colDef, newValue, api } = params;
    const rowId = data.LOT_ID || data.ID || params.node.id;
    const field = colDef.field;
    const isBusinessFieldChange = !SYSTEM_FIELDS.includes(field);
    // MODIFIED_BY / MODIFIED_DATE stay as loaded: MODIFIED_DATE is the
    // concurrency token the save sends back, and the writer stamps both.

    // Reactive flags: editing a spread/delta input recomputes this row's derived
    // SPRD_FLG / OTTI_FLG so the grid stays consistent without a separate "Run
    // Flags" step. computeRowFlags is the single source of truth (mirrors the
    // backend). AG Grid has already applied newValue to data[field], so we read
    // the flags off data; only the flag cells that actually change are tracked.
    const recomputedFlagFields = [];
    if (isBusinessFieldChange && FLAG_INPUT_FIELDS.includes(field)) {
      // Only the flags computeRowFlags can derive (OTTI_FLG needs both deltas).
      Object.entries(computeRowFlags(data)).forEach(([flagField, flagValue]) => {
        if (data[flagField] !== flagValue) {
          data[flagField] = flagValue;
          recomputedFlagFields.push(flagField);
        }
      });
      if (recomputedFlagFields.length && api?.getRowNode) {
        const rowNode = api.getRowNode(rowId);
        if (rowNode) {
          api.refreshCells({ rowNodes: [rowNode], columns: recomputedFlagFields, force: true });
        }
      }
    }

    const updatedData = ottiData.map(row => {
      if (row === data) {
        const updatedRow = { ...row, [field]: newValue };
        recomputedFlagFields.forEach((flagField) => {
          updatedRow[flagField] = data[flagField];
        });
        return updatedRow;
      }
      return row;
    });

    setOttiData(updatedData);

    const { sp: structuredProducts, nonSp: nonStructuredProducts } = partitionOttiBySecOwner(updatedData);

    let updatedChangedCells = changedCells;
    if (isBusinessFieldChange) {
      setChangedRows(prev => new Set(prev).add(rowId));
      const newChangedCells = new Map(changedCells);
      if (!newChangedCells.has(rowId)) newChangedCells.set(rowId, new Set());
      newChangedCells.get(rowId).add(field);
      recomputedFlagFields.forEach((flagField) => newChangedCells.get(rowId).add(flagField));
      updatedChangedCells = newChangedCells;
      setChangedCells(newChangedCells);
    }

    setSpData(structuredProducts);
    setNonSpData(nonStructuredProducts);

    const spColumns = getColumnDefs('sp', updatedChangedCells, newLotIds);
    const nonSpColumns = getColumnDefs('nonsp', updatedChangedCells, newLotIds);
    setSpColumnDefs(spColumns);
    setNonSpColumnDefs(nonSpColumns);
    setHasUnsavedChanges(true);
  }, [ottiData, getColumnDefs, changedCells, newLotIds]);

  // Derive displayed rows for each grid. When showMissingOnly is ON, show only
  // rows missing a comment; when OFF, show the full split. Export All and
  // Export Needs-Comment use the full datasets (ottiData), never these derived
  // views; they borrow the SP grid and put these rows back afterwards.
  const displayedSpData = useMemo(
    () => (showMissingOnly ? spData.filter(isMissingComment) : spData),
    [showMissingOnly, spData]
  );
  const displayedNonSpData = useMemo(
    () => (showMissingOnly ? nonSpData.filter(isMissingComment) : nonSpData),
    [showMissingOnly, nonSpData]
  );

  // Export functions
  // Export All / Export Needs-Comment load their rows into the SP grid, whose
  // quick filter and column filters still apply: `exportedRows: 'all'` exports
  // every loaded row regardless, so no lot is silently left out of the file.
  const exportAllToExcel = useCallback(async () => {
    if (spGridRef.current) {
      try {
        setIsExporting(true);
        const allColumns = getColumnDefs('all', new Map(), newLotIds, true);
        spGridRef.current.api.setGridOption('rowData', ottiData);
        spGridRef.current.api.setGridOption('columnDefs', allColumns);
        spGridRef.current.api.exportDataAsExcel({
          fileName: `OTTI_All_${selectedDate}.xlsx`,
          exportedRows: 'all',
        });
        setTimeout(() => {
          spGridRef.current.api.setGridOption('rowData', displayedSpData);
          spGridRef.current.api.setGridOption('columnDefs', spColumnDefs);
        }, 100);
      } finally {
        setTimeout(() => setIsExporting(false), 500);
      }
    }
  }, [getColumnDefs, newLotIds, ottiData, selectedDate, displayedSpData, spColumnDefs]);

  const exportSpToExcel = useCallback(async () => {
    if (spGridRef.current) {
      try {
        setIsExporting(true);
        const spColumns = getColumnDefs('sp', new Map(), newLotIds, true);
        spGridRef.current.api.setGridOption('columnDefs', spColumns);
        spGridRef.current.api.exportDataAsExcel({ fileName: `OTTI_SP_${selectedDate}.xlsx` });
        setTimeout(() => spGridRef.current.api.setGridOption('columnDefs', spColumnDefs), 100);
      } finally {
        setTimeout(() => setIsExporting(false), 500);
      }
    }
  }, [getColumnDefs, newLotIds, selectedDate, spColumnDefs]);

  const exportNonSpToExcel = useCallback(async () => {
    if (nonSpGridRef.current) {
      try {
        setIsExporting(true);
        const nonSpColumns = getColumnDefs('nonsp', new Map(), newLotIds, true);
        nonSpGridRef.current.api.setGridOption('columnDefs', nonSpColumns);
        nonSpGridRef.current.api.exportDataAsExcel({ fileName: `OTTI_NonSP_${selectedDate}.xlsx` });
        setTimeout(() => nonSpGridRef.current.api.setGridOption('columnDefs', nonSpColumnDefs), 100);
      } finally {
        setTimeout(() => setIsExporting(false), 500);
      }
    }
  }, [getColumnDefs, newLotIds, selectedDate, nonSpColumnDefs]);

  // Export the subset of rows that are missing a comment to Excel.
  // Uses the temporary-grid export pattern: set rowData + minimal columnDefs on
  // spGridRef, export, then restore the rows the grid displayed via setTimeout —
  // same approach as exportAllToExcel. The export always operates on the full
  // dataset, not the filtered (showMissingOnly / quick filter) view.
  const exportNeedsCommentToExcel = useCallback(async () => {
    if (!spGridRef.current) return;
    const missingRows = needsCommentRows(ottiData);
    if (missingRows.length === 0) return;

    try {
      setIsExporting(true);
      const exportColumns = ['LOT_ID', 'ID', 'DES', 'SEC_OWNER', 'COMMENTS'].map(field => {
        const fieldDef = COLUMN_DEFINITIONS[field];
        return createColumnDef(field, fieldDef?.type || 'VARCHAR', false, new Map(), new Set(), true);
      });
      spGridRef.current.api.setGridOption('rowData', missingRows);
      spGridRef.current.api.setGridOption('columnDefs', exportColumns);
      spGridRef.current.api.exportDataAsExcel({
        fileName: `OTTI_Needs_Comment_${selectedDate}.xlsx`,
        exportedRows: 'all',
      });
      setTimeout(() => {
        spGridRef.current.api.setGridOption('rowData', displayedSpData);
        spGridRef.current.api.setGridOption('columnDefs', spColumnDefs);
      }, 100);
    } finally {
      setTimeout(() => setIsExporting(false), 500);
    }
  }, [ottiData, selectedDate, displayedSpData, spColumnDefs, createColumnDef]);

  // Run OTTI processing
  const handleRunOtti = useCallback(async () => {
    if (!userId) {
      setError("User ID is required to run OTTI processing.");
      return;
    }
    if (!selectedDate) {
      setError("Please select an as of date to run OTTI processing.");
      return;
    }
    if (loadedDateRef.current !== selectedDate) {
      setError(GRID_NOT_LOADED_MESSAGE);
      return;
    }

    // Completion signal: the run is done once the latest modified_date for this
    // date advances past the value captured before firing.
    const readLatest = async () => {
      try {
        const res = await getRunStatus(selectedDate);
        return res?.data?.latest_modified ?? null;
      } catch {
        return null;
      }
    };

    // Unsaved edits as of launch. A run never saves them, so every reload after
    // it (success, gateway-poll completion, or a 409) re-applies them on top of
    // the run's rows, as pending changes the user still has to Save.
    const pendingEdits = {
      rows: ottiData.filter(row => changedRows.has(rowKey(row))),
      changedRows,
      changedCells,
    };
    const reloadKeepingEdits = () =>
      loadOttiData(selectedDate, changedRows.size > 0 ? { keepEdits: pendingEdits } : {});

    const notifyRunExceptions = (rows) => {
      if (!Array.isArray(rows)) {
        showNotification(
          'OTTI run completed, but the results could not be refreshed or verified. Refresh to see them.',
          'warning'
        );
        return true;
      }
      const list = rows;
      const exceptionLots = list
        .filter((r) => String(r?.OTTI_FLG || '').trim() === 'Cashflow Exception' && r?.LOT_ID)
        .map((r) => String(r.LOT_ID).trim());
      if (exceptionLots.length > 0) {
        const noun = exceptionLots.length === 1 ? 'lot' : 'lots';
        showNotification(
          `OTTI run completed with cashflow exceptions for ${exceptionLots.length} ${noun} (${exceptionLots.join(', ')}).`,
          'warning'
        );
        return true;
      }
      return false;
    };

    try {
      setIsRunningOtti(true);
      setError(null);
      const baseline = await readLatest();
      // Capture before runOtti: a 5xx can arrive minutes after the proc
      // recorded FAILED, so a post-reject timestamp would miss that row.
      const launchedAt = Date.now();
      const runId = (
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `otti-${launchedAt}-${Math.random().toString(36).slice(2)}`
      );

      try {
        await runOtti(userId, selectedDate, runId);
        const reloadedRows = await reloadKeepingEdits();
        await loadRunHistory(selectedDate);
        notifyRunExceptions(reloadedRows);
      } catch (err) {
        console.error("Error running OTTI:", err);
        // Permission / bad-request failures are real — surface them immediately.
        if (notifyPermissionError(showNotification, err)) {
          return;
        }
        const status = err?.response?.status;
        const isGatewayTimeout = err?.code === 'ECONNABORTED' || (status >= 500);
        if (!isGatewayTimeout) {
          // A 409 (a lot changed while the run was building, or the build's
          // run date did not match) means nothing was saved, so show the latest
          // rows, with this user's unsaved edits still applied, before they run
          // again. (Reload first: loadOttiData clears the error banner.)
          if (isStaleRowsError(err)) {
            await reloadKeepingEdits();
          }
          setError(getWriteErrorMessage(err, "Failed to run OTTI processing. Please try again."));
          await loadRunHistory(selectedDate);
          return;
        }
        // The proxy/gateway gave up but Beacon is still running. Poll /run-status
        // until the run commits (latest modified_date advances past baseline).
        // Also watch telemetry so a real proc failure is not a 10-minute fake wait.
        showNotification(
          "OTTI is taking a while — it's still processing on the server. Waiting for it to finish…",
          "info"
        );
        for (let attempt = 0; attempt < RUN_POLL_MAX_ATTEMPTS; attempt++) {
          await sleep(RUN_POLL_INTERVAL_MS);
          const latest = await readLatest();
          if (latest && latest !== baseline) {
            const reloadedRows = await reloadKeepingEdits();
            await loadRunHistory(selectedDate);
            if (!notifyRunExceptions(reloadedRows)) {
              showNotification("OTTI run complete.", "success");
            }
            return;
          }
          try {
            const historyRes = await getRunHistory(selectedDate);
            const runs = Array.isArray(historyRes.data) ? historyRes.data : [];
            const ownFailure = runs.find((run) => {
              const status = String(run?.STATUS || '').toUpperCase();
              return status === 'FAILED' && String(run?.RUN_ID || '') === runId;
            });
            if (ownFailure) {
              setError(ownFailure.NOTES || "Failed to run OTTI processing. Please try again.");
              await loadRunHistory(selectedDate);
              return;
            }
          } catch {
            // Telemetry is advisory during the latest_modified wait.
          }
        }
        setError(
          "OTTI is still running on the server. Refresh in a few minutes to see the results."
        );
      }
    } finally {
      setIsRunningOtti(false);
    }
  }, [userId, selectedDate, ottiData, changedRows, changedCells, loadOttiData, loadRunHistory, showNotification]);

  // Format fields for database
  const formatFieldsForDatabase = (data) => {
    const dateFields = ['DT', 'MDT', 'LD_DT'];
    return data.map(row => {
      const formattedRow = { ...row };
      dateFields.forEach(field => {
        if (formattedRow[field]) {
          if (typeof formattedRow[field] === 'string' && formattedRow[field].includes('T')) return;
          if (typeof formattedRow[field] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(formattedRow[field])) {
            formattedRow[field] = new Date(formattedRow[field] + 'T00:00:00.000Z').toISOString();
          } else if (formattedRow[field] instanceof Date) {
            formattedRow[field] = formattedRow[field].toISOString();
          } else if (typeof formattedRow[field] === 'string' && formattedRow[field].includes('GMT')) {
            formattedRow[field] = new Date(formattedRow[field]).toISOString();
          }
        }
      });
      if (formattedRow['KEEP_COMMENT'] !== undefined && formattedRow['KEEP_COMMENT'] !== null) {
        if (typeof formattedRow['KEEP_COMMENT'] === 'string') {
          formattedRow['KEEP_COMMENT'] = formattedRow['KEEP_COMMENT'] === 'Yes';
        }
      }
      return formattedRow;
    });
  };

  // Save OTTI data
  const handleSaveOtti = useCallback(async () => {
    // Checked first: rows (and edits) left from another date must never save.
    if (loadedDateRef.current !== selectedDate) {
      setError(GRID_NOT_LOADED_MESSAGE);
      return;
    }
    if (!hasUnsavedChanges || changedRows.size === 0) {
      setError("No changes to save.");
      return;
    }

    const changedData = ottiData.filter(row => changedRows.has(rowKey(row)));
    try {
      setIsSaving(true);
      setError(null);
      // Each row carries the MODIFIED_DATE it was loaded with (null for an
      // uploaded new lot); the writer stamps MODIFIED_BY / MODIFIED_DATE.
      // `?? null`: withLoadedModifiedDate defaults a missing loadedRow to the
      // row itself, and a row this grid never loaded must send null.
      const payload = changedData.map(row =>
        withLoadedModifiedDate(row, loadedRowsRef.current.get(rowKey(row)) ?? null));
      const formattedData = formatFieldsForDatabase(payload);
      await saveOtti(formattedData);
      await loadOttiData(selectedDate);
    } catch (err) {
      console.error("Error saving OTTI data:", err);
      if (isStaleRowsError(err)) {
        // Someone else saved one of these lots first. Nothing was saved: show
        // their data with this user's edits still applied, and let them
        // re-save deliberately. Never retry with the refreshed token here.
        await loadOttiData(selectedDate, {
          keepEdits: { rows: changedData, changedRows, changedCells },
        });
        setError(getWriteErrorMessage(err, VERSION_CONFLICT_MESSAGE));
      } else if (!notifyPermissionError(showNotification, err)) {
        setError(getWriteErrorMessage(err, "Failed to save OTTI data. Please try again."));
      }
    } finally {
      setIsSaving(false);
    }
  }, [hasUnsavedChanges, changedRows, changedCells, ottiData, selectedDate, loadOttiData, showNotification]);

  // Apply the detected "Lot Level Detail" / "Summary" sheets to the grid as
  // pending changes (mirrors secmaster's client-side merge). `rowSets` holds one
  // array of rows per sheet, keyed by the workbook's human headers; we normalize
  // them to DB columns, filter to the recognized schema fields, coerce values
  // to the types a live edit stores, then upsert into the grid. Every sheet
  // merges into the same working copy and state is set once, so a second sheet
  // builds on the first instead of overwriting it. Nothing is sent to the
  // server here — the user persists via the existing Save button
  // (handleSaveOtti -> /save-otti). Returns a summary object so the caller can
  // build the aggregate per-sheet notification.
  const applyLotDetailRows = useCallback((rowSets) => {
    const sheets = (rowSets || []).filter((rawRows) => rawRows && rawRows.length > 0);
    if (sheets.length === 0) {
      return { updatedCount: 0, newCount: 0, invalidValueCount: 0 };
    }

    // OTTI's grid key matches getRowId: LOT_ID, falling back to ID.
    const keyOf = rowKey;

    // Normalize human headers (and direct DB column names) to internal DB
    // columns, then drop any column the schema does not recognize.
    const uploadedSets = sheets.map((rawRows) => {
      const uploadedRows = rawRows
        .map((row) => {
          const mappedRow = normalizeRowKeys(row, ottiColumnNormalizer);
          const filteredRow = {};
          Object.entries(mappedRow).forEach(([key, value]) => {
            if (OTTI_IMPORT_FIELDS.has(key)) {
              filteredRow[key] = value;
            }
          });
          return filteredRow;
        })
        .filter((row) => keyOf(row));
      if (uploadedRows.length === 0) {
        throw new Error('Lot Level Detail must contain a Lot ID or ID for each row');
      }
      return uploadedRows;
    });

    // Non-empty values that failed to coerce: skipped (an existing lot keeps
    // its stored value, a new lot gets null) and surfaced in the notification.
    let invalidValueCount = 0;
    const newChangedRows = new Set(changedRows);
    const newChangedCells = new Map(changedCells);
    const markChanged = (key, fields) => {
      const cellSet = new Set(newChangedCells.get(key) || []);
      fields.forEach((field) => cellSet.add(field));
      newChangedRows.add(key);
      newChangedCells.set(key, cellSet);
    };

    const onNewRow = (row) => {
      const newRow = {};
      Object.entries(row).forEach(([field, raw]) => {
        const { value, invalid } = normalizeImportValue(field, raw);
        if (invalid) invalidValueCount += 1;
        newRow[field] = invalid ? null : value;
      });
      // No deltas until its first Run OTTI: OTTI_FLG stays null for the run to set.
      Object.assign(newRow, { OTTI_FLG: null }, computeRowFlags(newRow));
      markChanged(keyOf(newRow), Object.keys(newRow).filter((field) => !SYSTEM_FIELDS.includes(field)));
      // A lot the server has never saved: no token (the save sends null)
      // and nothing to show until the writer stamps it.
      return { ...newRow, MODIFIED_BY: null, MODIFIED_DATE: null };
    };

    const onUpdateRow = (existingRow, importedRow) => {
      const updatedRow = { ...existingRow };
      const changedFields = new Set();

      Object.entries(importedRow).forEach(([field, raw]) => {
        if (SYSTEM_FIELDS.includes(field)) return;
        const { value, invalid } = normalizeImportValue(field, raw);
        if (invalid) {
          invalidValueCount += 1;
          return;
        }
        if (importValueChanged(field, existingRow[field], value)) {
          updatedRow[field] = value;
          changedFields.add(field);
        }
      });

      // SPRD_FLG / OTTI_FLG are always derived from the merged row
      // (computeRowFlags), never taken from the sheet; a flag cell is tracked
      // (highlighted, saved) only when its derived value differs from the row's.
      Object.entries(computeRowFlags(updatedRow)).forEach(([flagField, flagValue]) => {
        if (flagValue !== existingRow[flagField]) {
          updatedRow[flagField] = flagValue;
          changedFields.add(flagField);
        }
      });

      if (changedFields.size > 0) {
        markChanged(keyOf(existingRow), changedFields);
      }
      return updatedRow;
    };

    let mergedData = ottiData;
    let newCount = 0;
    let updatedCount = 0;
    uploadedSets.forEach((uploadedRows) => {
      // Match on rowKey (LOT_ID || ID), the key saves and getRowId use: on
      // 'LOT_ID' alone every ID-only row would share the undefined key. The key
      // rides on a Symbol property (invisible to Object.entries and JSON) and is
      // stripped from every merged row; a keyless existing row gets a unique one.
      const result = mergeRows({
        uploadedRows: uploadedRows.map((row) => ({ ...row, [MERGE_KEY]: rowKey(row) })),
        existingRows: mergedData.map((row) => ({ ...row, [MERGE_KEY]: rowKey(row) || Symbol('unkeyed') })),
        matchField: MERGE_KEY,
        mergeStrategy: 'upsert',
        onNewRow,
        onUpdateRow,
      });
      mergedData = result.mergedData.map(withoutMergeKey);
      newCount += result.newCount;
      updatedCount += result.updatedCount;
    });

    // Recompute the SP / Non-SP split and column defs from the merged data,
    // exactly like loadOttiData / onCellValueChanged do.
    const { sp: structuredProducts, nonSp: nonStructuredProducts } = partitionOttiBySecOwner(mergedData);

    setOttiData(mergedData);
    setSpData(structuredProducts);
    setNonSpData(nonStructuredProducts);
    setChangedRows(newChangedRows);
    setChangedCells(newChangedCells);
    setSpColumnDefs(getColumnDefs('sp', newChangedCells, newLotIds));
    setNonSpColumnDefs(getColumnDefs('nonsp', newChangedCells, newLotIds));

    if (newCount > 0 || updatedCount > 0) {
      setHasUnsavedChanges(true);
    }

    return { updatedCount, newCount, invalidValueCount };
  }, [ottiData, changedRows, changedCells, getColumnDefs, newLotIds]);

  // Handle file upload — DYNAMIC MULTI-SHEET routing. The analyst's OTTI
  // workbook ships up to 8 sheets; we detect which are present (by name, with a
  // column-signature fallback) and route each to its destination:
  //   - Lot Level Detail / Summary -> grid merge (pending changes, Save later)
  //   - Current Spread             -> POST /upload-current-spreads
  //   - Ratings Downgrades         -> POST /upload-ratings-downgrades (downgrades only)
  //   - everything else (reference / Snowflake-backed) -> skipped
  // Any subset is accepted (including a single sheet). Results are aggregated
  // into one per-sheet notification.
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (loadedDateRef.current !== selectedDate) {
      setError(GRID_NOT_LOADED_MESSAGE);
      event.target.value = '';
      return;
    }

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel', '.xlsx', '.xls'
    ];

    if (!allowedTypes.some(type => file.type === type || file.name.toLowerCase().endsWith(type))) {
      setError("Please select a valid Excel file (.xlsx or .xls)");
      event.target.value = '';
      return;
    }

    if (!userId) {
      setError("User ID is required for file upload");
      event.target.value = '';
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      // Parse the full workbook (all sheets) in the browser.
      const { sheetNames, sheetsData } = await parseExcelWorkbook(file);
      if (!sheetNames || sheetNames.length === 0) {
        throw new Error('No sheets found in the uploaded file');
      }

      // Detect each present sheet's type and parse its data rows to objects.
      const detected = detectAndParse({ sheetNames, sheetsData });
      const routed = detected.filter(s => s.type !== 'unknown');
      const skipped = detected.filter(s => s.type === 'unknown');

      if (routed.length === 0) {
        showNotification('No recognized OTTI sheets found in the uploaded file', 'warning');
        return;
      }

      // Per-sheet result fragments for the aggregate notification.
      const resultParts = [];
      let uploadedCurrentSpreads = false;
      // All lot_detail sheets (e.g. both "Lot Level Detail" and "Summary") go
      // through ONE applyLotDetailRows call: it reads this render's grid state,
      // so a second call would overwrite the first call's edits.
      const lotDetailSheets = routed.filter(s => s.type === 'lot_detail');
      let lotDetailApplied = false;

      for (const sheet of routed) {
        if (sheet.type === 'current_spread') {
          const payload = mapCurrentSpreadPayload(sheet.rows, selectedDate);
          if (payload.length > 0) {
            await uploadCurrentSpreads(payload);
            uploadedCurrentSpreads = true;
          }
          resultParts.push(`${sheet.sheetName}: ${payload.length} uploaded`);
        } else if (sheet.type === 'ratings_downgrades') {
          const payload = mapRatingsDowngradePayload(sheet.rows, selectedDate);
          if (payload.length > 0) {
            await uploadRatingsDowngrades(payload);
          }
          resultParts.push(`${sheet.sheetName}: ${payload.length} uploaded`);
        } else if (sheet.type === 'lot_detail' && !lotDetailApplied) {
          lotDetailApplied = true;
          const { updatedCount, newCount, invalidValueCount } = applyLotDetailRows(
            lotDetailSheets.map(s => s.rows)
          );
          const invalidNote = invalidValueCount > 0
            ? ` (${invalidValueCount} unparseable values skipped)`
            : '';
          resultParts.push(
            `${lotDetailSheets.map(s => s.sheetName).join(' + ')}: ${updatedCount} updated/${newCount} new (click Save)${invalidNote}`
          );
        }
      }

      if (skipped.length > 0) {
        resultParts.push(`Skipped: ${skipped.map(s => s.sheetName).join(', ')}`);
      }

      if (uploadedCurrentSpreads) {
        await loadSpreadUploadStatus(selectedDate);
      }

      showNotification(resultParts.join(' · '), 'success');
    } catch (err) {
      console.error("Error uploading file:", err);
      if (isStaleRowsError(err)) {
        // A concurrent spread / downgrade upload won: nothing in this sheet was
        // saved. Refresh the status so the user sees the other upload.
        await loadSpreadUploadStatus(selectedDate);
        setError(getWriteErrorMessage(err, VERSION_CONFLICT_MESSAGE));
      } else if (!notifyPermissionError(showNotification, err)) {
        setError(getWriteErrorMessage(err, `Failed to import file: ${err.message}`));
      }
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  }, [userId, selectedDate, applyLotDetailRows, showNotification, loadSpreadUploadStatus]);

  const isAnyOperationRunning = isRunningOtti || isSaving || isUploading || isExporting;

  // Derive missing-comment counts from the full dataset (not the filtered view).
  const missingCommentCount = ottiData.filter(isMissingComment).length;
  const newLotsMissingCommentCount = ottiData.filter(
    row => newLotIds.has(row.LOT_ID) && isMissingComment(row)
  ).length;

  // Per-user grid layout persistence. We own these hooks here (rather than
  // letting GridConfigMenu instantiate them internally) so we can bridge
  // each gridConfigs.onGridReady into the matching PamGrid's onGridReady —
  // without that bridge, a grid that mounts after the configs response would
  // miss the default-layout auto-apply. One hook per grid, each with its own
  // ref and a distinct, descriptive gridId.
  const spGridConfigs = useGridConfigs(api, {
    app: 'otti',
    gridId: 'impairments',
    gridApiRef: spGridRef,
  });
  const nonSpGridConfigs = useGridConfigs(api, {
    app: 'otti',
    gridId: 'impairments-non-sp',
    gridApiRef: nonSpGridRef,
  });

  const loadingMessage = isLoadingDates
    ? 'Loading available dates...'
    : isLoading
    ? 'Loading OTTI data...'
    : isRunningOtti
    ? 'Processing...'
    : isSaving
    ? 'Saving...'
    : isUploading
    ? 'Uploading...'
    : 'Exporting...';

  // Drive the grid loading overlay from data/date loads too (the standalone
  // center spinner was removed with the bespoke chrome).
  const gridIsLoading = isAnyOperationRunning || isLoading || isLoadingDates;
  // False after a failed load of a newly selected date: the grid then holds no
  // rows for it, so Run / Upload / Save stay off until a reload succeeds.
  const isSelectedDateLoaded = Boolean(selectedDate) && loadedDate === selectedDate;

  // Each grid's toolbar holds ONLY its own buttons: its export + its config menu.
  // The shared controls (date, Run OTTI, Upload, Save, Refresh, quick filter,
  // Export All / Show-Missing / Export Needs-Comment) live in the shared toolbar
  // above both grids.
  const spToolbarButtons = [
    {
      label: 'Export SP',
      icon: <DownloadIcon />,
      onClick: exportSpToExcel,
      tooltip: 'Export the Structured Products grid',
      disabled: spData.length === 0 || isAnyOperationRunning,
    },
  ];
  const spToolbarComponents = [<GridConfigMenu key="grid-config-menu-sp" controller={spGridConfigs} />];

  const nonSpToolbarButtons = [
    {
      label: 'Export Non-SP',
      icon: <DownloadIcon />,
      onClick: exportNonSpToExcel,
      tooltip: 'Export the Non-SP grid',
      disabled: nonSpData.length === 0 || isAnyOperationRunning,
    },
  ];
  const nonSpToolbarComponents = [<GridConfigMenu key="grid-config-menu-non-sp" controller={nonSpGridConfigs} />];

  return (
    <Box>
      {/* Hidden input for the Upload toolbar button */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {selectedDate && (
        <Paper data-testid="otti-run-history" sx={{ mb: 2, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>OTTI Run History</Typography>
          {runHistory.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading OTTI run history…
              </Typography>
            </Box>
          ) : runHistory.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => loadRunHistory(selectedDate)}>
                  Retry
                </Button>
              }
            >
              Could not load OTTI run history
            </Alert>
          ) : runHistory.runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No OTTI runs for this month
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Ended</TableCell>
                    <TableCell>Rows</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runHistory.runs.map((run, idx) => (
                    <TableRow key={`${run.STARTED_AT || 'run'}-${idx}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={run.STATUS || ''}
                          color={runStatusChipColor(run.STATUS)}
                        />
                      </TableCell>
                      <TableCell>{run.USER || ''}</TableCell>
                      <TableCell>
                        {run.STARTED_AT ? (formatDateTimeForDisplay(run.STARTED_AT) || run.STARTED_AT) : ''}
                      </TableCell>
                      <TableCell>
                        {run.ENDED_AT ? (formatDateTimeForDisplay(run.ENDED_AT) || run.ENDED_AT) : ''}
                      </TableCell>
                      <TableCell>{run.ROW_COUNT == null ? '' : run.ROW_COUNT}</TableCell>
                      <TableCell>{run.NOTES || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {selectedDate && (
        <Alert
          data-testid="current-spread-upload-status"
          severity={spreadUploadStatus.error
            ? 'info'
            : spreadUploadStatus.uploaded
              ? 'success'
              : 'warning'}
          sx={{ mb: 2 }}
        >
          {spreadUploadStatus.asofdt !== selectedDate || !spreadUploadStatus.checked
            ? 'Checking whether current spreads have been uploaded...'
            : spreadUploadStatus.error
              ? `Could not verify current spread uploads for ${formatDate(selectedDate, 'MM/DD/YYYY')}.`
              : spreadUploadStatus.uploaded
                ? `Current spreads uploaded for ${formatDate(selectedDate, 'MM/DD/YYYY')} (${spreadUploadStatus.rowCount} active rows${spreadUploadStatus.latestUploaded ? `; last uploaded ${formatDateTimeForDisplay(spreadUploadStatus.latestUploaded)}` : ''}).`
                : `No current spread upload found for ${formatDate(selectedDate, 'MM/DD/YYYY')}. Run OTTI will use the Intex analytics fallback where available.`}
        </Alert>
      )}

      {/* Summary Statistics — above the toolbar so the dataset overview is the
          first thing visible on load. */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Summary Statistics</Typography>
        <MuiGrid container spacing={3}>
          <MuiGrid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
              <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>Structured Products</Typography>
              <Typography variant="body2" color="text.secondary">Records: <strong>{spData.length}</strong></Typography>
            </Box>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ p: 2, border: 1, borderColor: 'secondary.main', borderRadius: 1 }}>
              <Typography variant="subtitle1" color="secondary" sx={{ mb: 1, fontWeight: 'bold' }}>Non SP</Typography>
              <Typography variant="body2" color="text.secondary">Records: <strong>{nonSpData.length}</strong></Typography>
            </Box>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">Total Records</Typography>
            <Typography variant="h6">{ottiData.length}</Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">Total Columns (SP / Non-SP)</Typography>
            <Typography variant="h6">{spColumnDefs.length} / {nonSpColumnDefs.length}</Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">New Lot IDs (MoM)</Typography>
            <Typography variant="h6" sx={{ color: newLotIds.size > 0 ? '#2e7d32' : 'inherit' }}>
              {newLotIds.size} / {ottiData.length} ({ottiData.length > 0 ? ((newLotIds.size / ottiData.length) * 100).toFixed(1) : 0}%)
            </Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">Missing Comments</Typography>
            <Typography variant="h6" sx={{ color: missingCommentCount > 0 ? '#856404' : 'inherit' }}>
              {missingCommentCount} / {ottiData.length}
            </Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">New Lots Missing Comment</Typography>
            <Typography variant="h6" sx={{ color: newLotsMissingCommentCount > 0 ? 'error.main' : 'inherit' }}>
              {newLotsMissingCommentCount}
            </Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">As of Date</Typography>
            <Typography variant="h6">
              {typeof selectedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
                ? `${selectedDate.split('-')[1]}/${selectedDate.split('-')[2]}/${selectedDate.split('-')[0]}`
                : formatDate(selectedDate, 'MM/DD/YYYY')}
            </Typography>
          </MuiGrid>
        </MuiGrid>
      </Paper>

      {/* Shared toolbar — controls that act on both grids / the whole dataset.
          Per-grid buttons (Export SP / Export Non-SP + config) live on each grid. */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>As of Date</InputLabel>
          <Select
            value={selectedDate}
            label="As of Date"
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={gridIsLoading || availableDates.length === 0}
          >
            {availableDates.map((date) => (
              <MenuItem key={date} value={date}>
                {typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
                  ? `${date.split('-')[1]}/${date.split('-')[2]}/${date.split('-')[0]}`
                  : formatDate(date, 'MM/DD/YYYY')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <QuickFilter
          value={quickFilterText}
          onChange={setQuickFilterText}
          placeholder="Search all fields..."
          disabled={isAnyOperationRunning}
        />

        <Button
          variant="contained"
          startIcon={isRunningOtti ? <CircularProgress size={16} /> : <RunIcon />}
          onClick={handleRunOtti}
          disabled={gridIsLoading || !userId || !selectedDate || !isMaxAvailableDate() || !isSelectedDateLoaded}
        >
          {isRunningOtti ? 'Running...' : 'Run OTTI'}
        </Button>

        <Button
          variant="outlined"
          startIcon={isUploading ? <CircularProgress size={16} /> : <UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={gridIsLoading || !selectedDate || !userId || !isMaxAvailableDate() || !isSelectedDateLoaded}
        >
          {isUploading ? 'Uploading...' : 'Upload Excel'}
        </Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => selectedDate && loadOttiData(selectedDate)}
          disabled={gridIsLoading || !selectedDate}
        >
          Refresh
        </Button>

        <Button
          variant="contained"
          color={hasUnsavedChanges ? 'warning' : 'primary'}
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSaveOtti}
          disabled={gridIsLoading || !hasUnsavedChanges || changedRows.size === 0 || !isSelectedDateLoaded}
        >
          {changedRows.size > 0 ? `Save (${changedRows.size})` : 'Save'}
        </Button>

        <Button
          variant={showMissingOnly ? 'contained' : 'outlined'}
          color="warning"
          onClick={() => setShowMissingOnly(prev => !prev)}
          disabled={gridIsLoading || ottiData.length === 0}
        >
          {showMissingOnly ? 'Show All' : 'Show Missing Comments Only'}
        </Button>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportAllToExcel}
          disabled={gridIsLoading || ottiData.length === 0}
        >
          Export All
        </Button>

        <Button
          variant="outlined"
          color="warning"
          startIcon={<DownloadIcon />}
          onClick={exportNeedsCommentToExcel}
          disabled={gridIsLoading || missingCommentCount === 0}
        >
          Export Needs-Comment
        </Button>
      </Paper>

      <Paper component="section" role="region" aria-label="Grid highlight key" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Grid highlight key</Typography>
        <Box component="ul" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, listStyle: 'none', m: 0, p: 0 }}>
          {[
            { label: 'New to watchlist vs previous month (not necessarily a new purchase)', style: { backgroundColor: '#e8f5e8', border: '2px solid #4caf50' } },
            { label: 'Missing comment — blank or N/A; not an edit', style: { border: '1px dashed #856404' } },
            { label: 'Unsaved cell edit', style: { backgroundColor: '#ff9800' } },
            { label: 'Row contains unsaved edits', style: { backgroundColor: '#ffe6cc' } },
            { label: 'Spread flag', style: { backgroundColor: '#fff3cd' } },
            { label: 'Cashflow exception / flagged OTTI', style: { backgroundColor: '#ffebee' } },
          ].map(({ label, style }) => (
            <Box component="li" key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box aria-hidden="true" sx={{ width: 18, height: 18, flexShrink: 0, boxSizing: 'border-box', ...style }} />
              <Typography variant="caption">{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Structured Products grid — toolbar holds only its own export + config */}
      <Box sx={{ mb: 3 }}>
        <PamGrid
          ref={spGridRef}
          rowData={displayedSpData}
          columnDefs={spColumnDefs}
          showToolbar
          title={`Structured Products (${spData.length})`}
          showExport={false}
          showQuickFilter={false}
          quickFilterText={quickFilterText}
          customToolbarButtons={spToolbarButtons}
          customToolbarComponents={spToolbarComponents}
          height="400px"
          rowHeight={32}
          getRowId={(data) => data.LOT_ID || data.ID}
          isLoading={gridIsLoading}
          loadingMessage={loadingMessage}
          onCellValueChanged={onCellValueChanged}
          onGridReady={spGridConfigs.onGridReady}
          onFilterChanged={spGridConfigs.onGridStateChanged}
          agGridProps={spGridConfigs.stashBindings}
          defaultColDef={{ flex: 1, minWidth: 100, cellStyle: { fontSize: '12px' } }}
        />
      </Box>

      {/* Non-SP grid */}
      <Box sx={{ mb: 3 }}>
        <PamGrid
          ref={nonSpGridRef}
          rowData={displayedNonSpData}
          columnDefs={nonSpColumnDefs}
          showToolbar
          title={`Non SP (${nonSpData.length})`}
          showExport={false}
          showQuickFilter={false}
          quickFilterText={quickFilterText}
          customToolbarButtons={nonSpToolbarButtons}
          customToolbarComponents={nonSpToolbarComponents}
          height="400px"
          rowHeight={32}
          getRowId={(data) => data.LOT_ID || data.ID}
          isLoading={gridIsLoading}
          loadingMessage={loadingMessage}
          onCellValueChanged={onCellValueChanged}
          onGridReady={nonSpGridConfigs.onGridReady}
          onFilterChanged={nonSpGridConfigs.onGridStateChanged}
          agGridProps={nonSpGridConfigs.stashBindings}
          defaultColDef={{ flex: 1, minWidth: 100, cellStyle: { fontSize: '12px' } }}
        />
      </Box>

      {/* Asset Class Assumptions Table */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>Asset Class Assumptions</Typography>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {assumptionsData[0].map((header, index) => (
                  <TableCell key={index} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.875rem' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {assumptionsData.slice(1).map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  {row.map((cell, cellIndex) => (
                    <TableCell key={cellIndex} sx={{ fontSize: '0.8rem', maxWidth: cellIndex === 0 ? '150px' : '300px', whiteSpace: 'pre-wrap', verticalAlign: 'top', lineHeight: 1.4 }}>
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default OttiGrid;
